import React, { useEffect, useState } from 'react';
import {
  LayoutDashboard, UserPlus, ListChecks, CalendarRange, Stethoscope, RefreshCw, DoorOpen, ScrollText,
  ShieldAlert, Clock, Plus, PenLine, Users,
} from 'lucide-react';
import { api, DEMO } from '../lib/api';
import { hasCap } from '../lib/capabilities';
import PhpIndividualTherapy from './PhpIndividualTherapy';
import PhpGroupResponses from './PhpGroupResponses';
import FunctioningOverview from './FunctioningOverview';
import PlanEditor, { emptyPlan } from './PlanEditor';
import TreatmentPlanReviewForm from './TreatmentPlanReviewForm';
import DiagnosisClassBadge from './DiagnosisClassBadge';
import './SectionPhpProgram.css';

// PSR (Psychosocial Rehabilitation) is a DISTINCT program, not a PHP subtype. It reuses the same
// canonical episode/admission/treatment-plan/therapy/PHQ-9/safety infrastructure but carries NONE of
// PHP's regulatory rules — no eligibility, no physician certification/recertification, no 20-hour
// intensity, no medical-necessity, no admission completeness. Therapists manage it via program.manage.
const SECTIONS = [
  { key: 'overview', label: 'Overview', icon: LayoutDashboard },
  { key: 'admission', label: 'Admission', icon: UserPlus },
  { key: 'treatment-plan', label: 'Treatment Plan', icon: ListChecks },
  { key: 'services', label: 'Services', icon: CalendarRange },
  { key: 'individual', label: 'Individual', icon: Stethoscope },
  { key: 'group', label: 'Group', icon: Users },
  { key: 'reviews', label: 'Reviews', icon: RefreshCw },
  { key: 'discharge', label: 'Discharge', icon: DoorOpen },
  { key: 'audit', label: 'Audit', icon: ScrollText },
];

const SERVICE_TYPES = {
  individual_therapy: 'Individual therapy', group_therapy: 'Group therapy', nursing: 'Nursing',
  physician: 'Physician', activity: 'Activity', psychoeducation: 'Psychoeducation', other: 'Other',
};
const SERVICE_STATUSES = { scheduled: 'Scheduled', rendered: 'Rendered', cancelled: 'Cancelled', no_show: 'No-show' };

const PSR_POLICY_LABELS = {
  program_definition: 'Program definition', service_intensity: 'Service intensity',
  staffing_requirements: 'Staffing requirements', review_cadence: 'Review cadence',
  discharge_criteria: 'Discharge criteria',
};

function statusBadge(status) {
  return <span className={`badge ${status === 'finalized' ? 'badge-ok' : status === 'voided' ? 'badge-neutral' : 'badge-warn'}`}>{status}</span>;
}

function splitLines(s) {
  return String(s || '').split(/\r?\n/).map(x => x.trim()).filter(Boolean);
}

function Card({ label, children }) {
  return <div className="php-ov-card"><span className="php-ov-label">{label}</span><div className="php-ov-value">{children}</div></div>;
}

export default function SectionPsrProgram({ patient, user, notify, navigate, openAssessments }) {
  const canManage = hasCap(user, 'program.manage');
  const [section, setSection] = useState('overview');

  return (
    <section className="content-card">
      <div className="content-heading">
        <div>
          <div className="eyebrow">PSYCHOSOCIAL REHABILITATION (PSR)</div>
          <h3>Psychosocial rehabilitation program</h3>
          <p>Episode, admission, treatment plan, services, reviews, and discharge — built on the single canonical enrollment record, as a program distinct from PHP. PSR carries none of PHP's regulatory rules (no eligibility, certification, or 20-hour intensity). Clinical screenings (PHQ-9) are read-only references.</p>
        </div>
      </div>

      <nav className="php-program-nav" aria-label="PSR program sections">
        {SECTIONS.map(s => {
          const Icon = s.icon;
          return (
            <button key={s.key} type="button" className={section === s.key ? 'active' : ''} onClick={() => setSection(s.key)}>
              <Icon size={15} /><span>{s.label}</span>
            </button>
          );
        })}
      </nav>

      <div className="php-program-body">
        {section === 'overview' && <Overview patient={patient} user={user} notify={notify} navigate={navigate} />}
        {section === 'admission' && <Admission patient={patient} user={user} notify={notify} canManage={canManage} />}
        {section === 'treatment-plan' && <TreatmentPlan patient={patient} user={user} notify={notify} navigate={navigate} canManage={canManage} />}
        {section === 'services' && <Services patient={patient} user={user} notify={notify} canManage={canManage} />}
        {section === 'individual' && hasCap(user, 'therapy.view') && <Individual patient={patient} user={user} notify={notify} openAssessments={openAssessments} />}
        {section === 'group' && hasCap(user, 'therapy.view') && <Group patient={patient} user={user} notify={notify} navigate={navigate} openAssessments={openAssessments} />}
        {section === 'reviews' && <Reviews patient={patient} user={user} notify={notify} canManage={canManage} />}
        {section === 'discharge' && <Discharge patient={patient} user={user} notify={notify} canManage={canManage} />}
        {section === 'audit' && <Audit patient={patient} user={user} notify={notify} />}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ Overview */

function Overview({ patient, user, notify, navigate }) {
  const [o, setO] = useState(null);

  useEffect(() => {
    if (DEMO) { setO(demoOverview(patient)); return; }
    api(`/patients/${patient.id}/psr/program`).then(setO).catch(e => notify(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id]);

  if (!o) return <p className="empty-copy">Loading program overview…</p>;
  const enr = o.enrollment;

  return (
    <div>
      {!enr && (
        <div className="php-empty-banner">
          <Clock size={18} />
          <div><strong>No PSR enrollment</strong><p>This patient has no psychosocial rehabilitation episode. Use Admission to create one.</p></div>
        </div>
      )}

      <div className="php-ov-grid">
        <Card label="Episode status">
          <span className={`badge ${enr ? (enr.status === 'active' ? 'badge-ok' : enr.status === 'discharged' ? 'badge-neutral' : 'badge-warn') : 'badge-neutral'}`}>{enr ? enr.status : 'none'}</span>
        </Card>
        <Card label="Admission date">{enr?.admitted_at || o.admission?.admitted_on || '—'}</Card>
        <Card label="Primary diagnosis">
          {o.primary_diagnosis ? <><span>{o.primary_diagnosis.code} · {o.primary_diagnosis.description}</span><DiagnosisClassBadge diagnosis={(patient.diagnoses || []).find(d => d.code === o.primary_diagnosis.code)} /></> : '—'}
        </Card>
        <Card label="Treating physician">
          {o.treating_physician ? <span>{o.treating_physician.name}{o.treating_physician.title ? ', ' + o.treating_physician.title : ''}</span> : '—'}
        </Card>
        <Card label="Primary therapist"><span>{o.primary_therapist || 'Not assigned'}</span></Card>
        <Card label="Treatment plan">
          {o.treatment_plan ? <span className={`badge ${o.treatment_plan.status === 'active' ? 'badge-ok' : 'badge-warn'}`}>{o.treatment_plan.status} · v{o.treatment_plan.version}</span> : '—'}
        </Card>
        <Card label="Next treatment plan review">
          {o.treatment_plan?.next_review_due_at ? new Date(o.treatment_plan.next_review_due_at).toLocaleDateString() : 'Not scheduled'}
        </Card>
        <Card label="Secondary diagnoses">
          {(o.secondary_diagnoses || []).length ? <span className="muted">{o.secondary_diagnoses.map(d => d.code).join(', ') || '—'}</span> : '—'}
        </Card>
        <Card label="Facility">{o.facility_name || '—'}</Card>
      </div>

      {o.psr_policy && (
        <div className="php-ov-section php-admission-status attention">
          <div className="content-heading"><div><h4>PSR policy / regulatory configuration</h4><p>These are facility-driven policy decisions the software must not assume. Configure them before relying on PSR program rules.</p></div></div>
          <ul className="php-checklist">
            {Object.entries(o.psr_policy).map(([k, v]) => (
              <li key={k} className="todo"><span className="php-checklist-mark">⚠</span><span>{PSR_POLICY_LABELS[k] || k} — {v}</span></li>
            ))}
          </ul>
        </div>
      )}

      {o.requires_action && o.requires_action.total > 0 && (
        <div className="php-ov-section php-requires-action">
          <div className="content-heading"><div><h4>Requires action</h4><p>Documentation that still needs a signature or completion. Cancelled, no-show, no-service, and voided records are excluded.</p></div><span className="badge badge-warn">{o.requires_action.total} item{o.requires_action.total === 1 ? '' : 's'}</span></div>
          <ul className="php-checklist">
            {o.requires_action.unsigned_group_responses > 0 && <li className="todo"><span className="php-checklist-mark">⚠</span><span>{o.requires_action.unsigned_group_responses} unsigned group response{o.requires_action.unsigned_group_responses === 1 ? '' : 's'}</span></li>}
            {o.requires_action.unsigned_individual_therapy > 0 && <li className="todo"><span className="php-checklist-mark">⚠</span><span>{o.requires_action.unsigned_individual_therapy} individual therapy note{o.requires_action.unsigned_individual_therapy === 1 ? '' : 's'} to sign</span></li>}
          </ul>
        </div>
      )}

      {(o.recent_treating_clinicians || []).length > 0 && (
        <div className="php-ov-section">
          <div className="content-heading"><div><h4>Recent treating clinicians</h4><p>Activity-derived from recent signed therapy and group documentation — not an assigned caseload.</p></div></div>
          <div className="php-clinician-list">
            {o.recent_treating_clinicians.map(c => <span key={c.name} className="badge badge-neutral">{c.name}{c.title ? ', ' + c.title : ''}</span>)}
          </div>
        </div>
      )}

      {o.open_safety_assessments?.length > 0 && (
        <div className="php-ov-section">
          <div className="content-heading"><div><h4>Open safety follow-up</h4><p>Read-only reference — the safety workflow lives on the patient's Safety Alerts, independent of PSR.</p></div></div>
          {o.open_safety_assessments.map(s => (
            <div key={s.id} className="php-safety-row">
              <ShieldAlert size={16} />
              <span className={`badge ${s.status === 'ESCALATED' ? 'badge-danger' : 'badge-warn'}`}>{s.status}</span>
              <span className="badge neutral">{s.priority}</span>
              <small>{s.trigger} · {s.created_at}</small>
            </div>
          ))}
        </div>
      )}

      <FunctioningOverview functioning={o.functioning} />

      {o.latest_phq9 && (
        <div className="php-ov-section">
          <div className="content-heading"><div><h4>Latest PHQ-9</h4><p>Screening only — never an eligibility, necessity, or level-of-care determination.</p></div></div>
          <div className="php-screening">
            <div className="php-screening-score"><strong>{o.latest_phq9.total}</strong><span>/ 27</span></div>
            <div className="php-screening-meta">
              <b>{o.latest_phq9.severity}</b>
              <small>Completed {new Date(o.latest_phq9.completed_at).toLocaleString()}</small>
              {o.phq9_change != null && <small>Change from previous: {o.phq9_change > 0 ? '+' : ''}{o.phq9_change}</small>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function demoOverview(patient) {
  return {
    enrollment: null, admission: null, treatment_plan: null, primary_diagnosis: null,
    secondary_diagnoses: [], treating_physician: null, primary_therapist: 'Not assigned',
    recent_treating_clinicians: [], facility_name: null, phq9_change: null,
    latest_phq9: (patient.phq9 || [])[0] || null, open_safety_assessments: [],
    requires_action: { unsigned_group_responses: 0, unsigned_individual_therapy: 0, total: 0 },
    psr_policy: {},
    functioning: null,
  };
}

/* ------------------------------------------------------------------ Admission */

function Admission({ patient, user, notify, canManage }) {
  const [data, setData] = useState({ admissions: [] });
  const [form, setForm] = useState(emptyAdmission());
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      if (DEMO) { setData({ admissions: [] }); return; }
      const a = await api(`/patients/${patient.id}/psr/admissions`);
      setData({ admissions: a.admissions || [] });
    } catch (err) { notify(err.message); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patient.id]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (DEMO) { notify('Admission recorded (demo).'); setForm(emptyAdmission()); return; }
      await api(`/patients/${patient.id}/psr/admit`, { method: 'POST', body: form });
      notify('Admission recorded.');
      setForm(emptyAdmission());
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  const physicians = (patient.care_team || []).filter(c => c.user_id);
  const diagnoses = (patient.diagnoses || []).filter(d => ['active', 'chronic'].includes(d.status));

  return (
    <div>
      <div className="content-heading"><div><h4>Admission records</h4><p>Admitting a patient creates or activates the canonical PSR enrollment. There is no PSR eligibility or certification requirement.</p></div></div>
      {data.admissions.length === 0 && <p className="empty-copy">No admission records yet.</p>}
      {data.admissions.map(a => (
        <div key={a.id} className="php-plan">
          <div className="php-plan-head">
            {statusBadge(a.status)}
            <strong>Version {a.version}</strong>
            <small>Admitted {a.admitted_on || '—'}</small>
            {a.finalized_at && <small>· Finalized {a.finalized_at}</small>}
          </div>
          <div className="php-problem">
            <strong className="php-problem-desc">{a.presenting_problem || '—'}</strong>
            <small>Admitting physician: {a.admitting_name || '—'} · Recorded by {a.creator_name || '—'}</small>
          </div>
        </div>
      ))}

      {canManage && (
        <form className="php-form section-space" onSubmit={submit}>
          <div className="content-heading"><div><h4>New admission</h4></div></div>
          <div className="form-grid">
            <label>Admission date<input type="date" value={form.admitted_on} onChange={set('admitted_on')} /></label>
            <label>Treating physician
              <select value={form.treating_physician_id} onChange={set('treating_physician_id')}>
                <option value="">— Unset —</option>
                {physicians.map(p => <option key={p.user_id} value={p.user_id}>{p.user_name}{p.user_title ? ' · ' + p.user_title : ''}</option>)}
              </select>
            </label>
            <label>Primary diagnosis
              <select value={form.primary_diagnosis_id} onChange={set('primary_diagnosis_id')}>
                <option value="">— Unset —</option>
                {diagnoses.map(d => <option key={d.id} value={d.id}>{d.code} · {d.description}</option>)}
              </select>
            </label>
            <label className="span2">Presenting problem<textarea rows={2} value={form.presenting_problem} onChange={set('presenting_problem')} maxLength={10000} /></label>
            <label className="span2">Clinical summary<textarea rows={2} value={form.clinical_summary} onChange={set('clinical_summary')} maxLength={10000} /></label>
            <label className="span2">Goals for program<textarea rows={2} value={form.goals_for_program} onChange={set('goals_for_program')} maxLength={10000} /></label>
            <label className="span2">Functional impairment<textarea rows={2} value={form.functional_impairment} onChange={set('functional_impairment')} maxLength={10000} /></label>
            <label className="span2">Support system<textarea rows={2} value={form.support_system} onChange={set('support_system')} maxLength={10000} /></label>
            <label className="span2">Clinical formulation<textarea rows={2} value={form.clinical_formulation} onChange={set('clinical_formulation')} maxLength={10000} /></label>
            <label className="span2">Expected improvement<textarea rows={2} value={form.expected_improvement} onChange={set('expected_improvement')} maxLength={10000} /></label>
            <label className="span2">Discharge considerations<textarea rows={2} value={form.discharge_considerations} onChange={set('discharge_considerations')} maxLength={10000} /></label>
            <label className="check-row"><input type="checkbox" checked={form.finalize} onChange={e => setForm(f => ({ ...f, finalize: e.target.checked }))} /><span>Finalize on save (captures an immutable snapshot of the reviewed canonical state)</span></label>
          </div>
          <button className="button primary" disabled={busy}>{busy ? 'Recording…' : 'Record admission'}</button>
        </form>
      )}
    </div>
  );
}

function emptyAdmission() {
  return { admitted_on: new Date().toISOString().slice(0, 10), treating_physician_id: '', primary_diagnosis_id: '', presenting_problem: '', clinical_summary: '', goals_for_program: '', functional_impairment: '', support_system: '', clinical_formulation: '', expected_improvement: '', discharge_considerations: '', finalize: true };
}

/* ------------------------------------------------------------- Treatment Plan */

function TreatmentPlan({ patient, user, notify, navigate, canManage }) {
  const [plans, setPlans] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(emptyPlan());
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      if (DEMO) { setPlans([]); setEnrollments([]); return; }
      const [p, e] = await Promise.all([
        api(`/patients/${patient.id}/php/treatment-plans`),
        api(`/patients/${patient.id}/psr/enrollments`),
      ]);
      setPlans(p.plans || []);
      setEnrollments(e.enrollments || []);
    } catch (err) { notify(err.message); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patient.id]);

  function startNew() { setForm(emptyPlan()); setEditing(true); }
  function startEdit(plan) {
    setForm({
      enrollment_id: plan.enrollment_id, plan_id: plan.id,
      diagnosis_ids: (plan.diagnoses || []).map(d => d.id),
      medication_effectiveness: plan.medication_effectiveness || '',
      agencies_involved: plan.agencies_involved || '',
      problems: (plan.problems || []).map(p => ({
        description: p.description, sort_order: p.sort_order,
        goals: (p.goals || []).map(g => ({
          description: g.description, target_date: g.target_date || '',
          objectives: (g.objectives || []).map(o => ({
            description: o.description, target_date: o.target_date || '', person_involved: o.person_involved || '', end_date: o.end_date || '',
            interventions: (o.interventions || []).map(i => ({ description: i.description, frequency: i.frequency || '', discipline: i.discipline || '', target_date: i.target_date || '' })),
          })),
        })),
      })),
    });
    setEditing(true);
  }

  async function save(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (DEMO) { notify('Treatment plan saved (demo).'); setEditing(false); return; }
      await api(`/patients/${patient.id}/php/treatment-plans`, { method: 'POST', body: form });
      notify('Treatment plan saved.');
      setEditing(false);
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  async function sign(planId) {
    setBusy(true);
    try {
      if (DEMO) { notify('Treatment plan signed (demo).'); return; }
      await api(`/patients/${patient.id}/php/treatment-plans/sign`, { method: 'POST', body: { plan_id: planId } });
      notify('Treatment plan signed.');
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  return (
    <div>
      <div className="content-heading">
        <div><h4>Treatment plans</h4><p>Problem → Goal → Objective → Intervention. A signed plan is never silently modified — edits create a new version.</p></div>
        <div className="php-head-actions">
          {canManage && !editing && <button type="button" className="button primary" onClick={startNew}><Plus size={14} /><span>New plan</span></button>}
        </div>
      </div>

      {plans.length === 0 && !editing && <p className="empty-copy">No treatment plans yet.</p>}

      {plans.map(plan => (
        <div key={plan.id} className="php-plan">
          <div className="php-plan-head">
            <span className={`badge ${plan.status === 'active' ? 'badge-ok' : plan.status === 'draft' ? 'badge-warn' : 'badge-neutral'}`}>{plan.status}</span>
            <strong>Version {plan.version}</strong>
            {plan.signed_at && <small>Signed {plan.signed_at}</small>}
            <div className="spacer" />
            {canManage && plan.status === 'draft' && <button type="button" className="button ghost" disabled={busy} onClick={() => sign(plan.id)}>Sign</button>}
            {canManage && <button type="button" className="button ghost" disabled={busy} onClick={() => startEdit(plan)}><PenLine size={14} /><span>Revise</span></button>}
          </div>
          {(plan.diagnoses || []).length > 0 && (
            <div className="php-problem"><span className="php-pe-label">Diagnoses (referenced)</span><span className="muted">{plan.diagnoses.map(d => `${d.code} · ${d.description}`).join('; ')}</span></div>
          )}
          {plan.medication_effectiveness && <div className="php-problem"><span className="php-pe-label">Medication effectiveness</span><span>{plan.medication_effectiveness}</span></div>}
          {plan.agencies_involved && <div className="php-problem"><span className="php-pe-label">Agencies involved</span><span>{plan.agencies_involved}</span></div>}
          {(plan.problems || []).map(p => (
            <div key={p.id} className="php-problem">
              <strong className="php-problem-desc">{p.description}</strong>
              {(p.goals || []).map(g => (
                <div key={g.id} className="php-goal">
                  <span>{g.description}{g.target_date ? <small> · by {g.target_date}</small> : null}</span>
                  {(g.objectives || []).map(o => (
                    <div key={o.id} className="php-obj">
                      <span>{o.description}{o.target_date ? <small> · by {o.target_date}</small> : null}{o.end_date ? <small> · until {o.end_date}</small> : null}{o.person_involved ? <small> · {o.person_involved}</small> : null}</span>
                      {(o.interventions || []).map(i => (
                        <div key={i.id} className="php-int"><span>{i.description}</span><small>{[i.frequency, i.discipline, i.target_date].filter(Boolean).join(' · ')}</small></div>
                      ))}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}
        </div>
      ))}

      {editing && (
        <form className="php-form section-space" onSubmit={save}>
          <div className="content-heading"><div><h4>{form.plan_id ? 'Revise plan' : 'New plan'}</h4></div></div>
          <label>Enrollment
            <select value={form.enrollment_id} onChange={e => setForm(f => ({ ...f, enrollment_id: e.target.value }))}>
              <option value="">— Select —</option>
              {enrollments.map(en => <option key={en.id} value={en.id}>{en.program_type} · admitted {en.admitted_at} · {en.status}</option>)}
            </select>
          </label>
          <fieldset className="php-form-group section-space">
            <legend>Diagnoses (referenced, never copied)</legend>
            {(patient.diagnoses || []).map(d => (
              <label key={d.id} className="check-row">
                <input type="checkbox" checked={(form.diagnosis_ids || []).includes(d.id)} onChange={e => setForm(f => ({ ...f, diagnosis_ids: e.target.checked ? [...(f.diagnosis_ids || []), d.id] : (f.diagnosis_ids || []).filter(x => x !== d.id) }))} />
                <span>{d.code} · {d.description}</span>
              </label>
            ))}
          </fieldset>
          <label className="span2">Medication effectiveness<textarea rows={2} value={form.medication_effectiveness} onChange={e => setForm(f => ({ ...f, medication_effectiveness: e.target.value }))} maxLength={8000} /></label>
          <label className="span2">Agencies involved<textarea rows={2} value={form.agencies_involved} onChange={e => setForm(f => ({ ...f, agencies_involved: e.target.value }))} maxLength={8000} /></label>
          <PlanEditor form={form} setForm={setForm} />
          <div className="php-plan-actions">
            <button className="button primary" disabled={busy}>{busy ? 'Saving…' : 'Save plan'}</button>
            <button type="button" className="button ghost" onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Services */

function Services({ patient, user, notify, canManage }) {
  const [services, setServices] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [form, setForm] = useState(emptyService());
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      if (DEMO) { setServices([]); setEnrollments([]); return; }
      const [s, e] = await Promise.all([
        api(`/patients/${patient.id}/php/daily-services`),
        api(`/patients/${patient.id}/psr/enrollments`),
      ]);
      const psrIds = new Set((e.enrollments || []).map(en => en.id));
      setServices((s.services || []).filter(sv => psrIds.has(sv.enrollment_id)));
      setEnrollments(e.enrollments || []);
    } catch (err) { notify(err.message); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patient.id]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (DEMO) { notify('Daily service saved (demo).'); setForm(emptyService()); return; }
      if (form.id) await api(`/patients/${patient.id}/php/daily-services/${form.id}`, { method: 'PATCH', body: form });
      else await api(`/patients/${patient.id}/php/daily-services`, { method: 'POST', body: form });
      notify('Daily service saved.');
      setForm(emptyService());
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  function edit(s) {
    setForm({ id: s.id, enrollment_id: s.enrollment_id, service_date: s.service_date, start_time: s.start_time || '', end_time: s.end_time || '', duration_minutes: s.duration_minutes, service_type: s.service_type, service_status: s.service_status, attended: s.attended, signature_status: s.signature_status || 'unsigned', cpt_code: s.cpt_code || '' });
  }

  return (
    <div>
      <div className="content-heading"><div><h4>Services</h4><p>Canonical service log for the PSR enrollment. PSR has no mandated 20-hour intensity; these rows are a record of services delivered, not a regulatory intensity engine.</p></div></div>

      {services.length === 0 && <p className="empty-copy">No services recorded.</p>}
      {services.length > 0 && (
        <div className="table-scroll"><table>
          <thead><tr><th>Date</th><th>Time</th><th>Type</th><th>Provider</th><th>Min</th><th>Status</th><th>Attended</th><th>Signature</th>{canManage && <th></th>}</tr></thead>
          <tbody>{services.map(s => (
            <tr key={s.id}>
              <td>{s.service_date}</td>
              <td>{[s.start_time, s.end_time].filter(Boolean).join('–') || '—'}</td>
              <td>{SERVICE_TYPES[s.service_type] || s.service_type}</td>
              <td>{s.provider_name || '—'}</td>
              <td>{s.duration_minutes}</td>
              <td><span className="badge neutral">{SERVICE_STATUSES[s.service_status] || s.service_status}</span></td>
              <td>{s.attended ? 'Yes' : 'No'}</td>
              <td>{s.signature_status}</td>
              {canManage && <td><button type="button" className="link" onClick={() => edit(s)}>Edit</button></td>}
            </tr>
          ))}</tbody>
        </table></div>
      )}

      {canManage && (
        <form className="php-form section-space" onSubmit={submit}>
          <div className="content-heading"><div><h4>{form.id ? 'Edit service' : 'Add service'}</h4></div></div>
          <div className="form-grid">
            <label>Enrollment
              <select value={form.enrollment_id} onChange={set('enrollment_id')}>
                <option value="">— Select —</option>
                {enrollments.map(en => <option key={en.id} value={en.id}>{en.program_type} · {en.admitted_at} · {en.status}</option>)}
              </select>
            </label>
            <label>Service date<input type="date" value={form.service_date} onChange={set('service_date')} /></label>
            <label>Type<select value={form.service_type} onChange={set('service_type')}>{Object.entries(SERVICE_TYPES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
            <label>Status<select value={form.service_status} onChange={set('service_status')}>{Object.entries(SERVICE_STATUSES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
            <label>Start time<input type="time" value={form.start_time} onChange={set('start_time')} /></label>
            <label>End time<input type="time" value={form.end_time} onChange={set('end_time')} /></label>
            <label>Duration (min)<input type="number" min="0" max="1440" value={form.duration_minutes} onChange={set('duration_minutes')} /></label>
            <label>CPT code<input value={form.cpt_code} onChange={set('cpt_code')} maxLength={20} /></label>
            <label>Attended<select value={form.attended} onChange={set('attended')}><option value="false">No</option><option value="true">Yes</option></select></label>
            <label>Signature<select value={form.signature_status} onChange={set('signature_status')}><option value="unsigned">Unsigned</option><option value="signed">Signed</option></select></label>
          </div>
          <button className="button primary" disabled={busy}>{busy ? 'Saving…' : form.id ? 'Update service' : 'Add service'}</button>
        </form>
      )}
    </div>
  );
}

function emptyService() { return { id: null, enrollment_id: '', service_date: new Date().toISOString().slice(0, 10), start_time: '', end_time: '', duration_minutes: 0, service_type: 'individual_therapy', service_status: 'scheduled', attended: 'false', signature_status: 'unsigned', cpt_code: '' }; }

/* ------------------------------------------------------------ Individual / Group */

function Individual({ patient, user, notify, openAssessments }) {
  return (
    <div>
      <div className="content-heading"><div><h4>Individual therapy documentation</h4><p>Individual (A/I/R/E). A signed note is immutable — corrections are addenda. Risk/safety fields are documentation only and never determine level of care.</p></div></div>
      <div className="php-program-body">
        <PhpIndividualTherapy patient={patient} user={user} notify={notify} openAssessments={openAssessments} programType="psr" />
      </div>
    </div>
  );
}

function Group({ patient, user, notify, navigate, openAssessments }) {
  return (
    <div>
      <div className="content-heading"><div><h4>Group therapy documentation</h4><p>Group therapy notes (A/I/R/E). A signed note is immutable — corrections are addenda. Risk/safety fields are documentation only and never determine level of care.</p></div></div>
      <div className="php-program-body">
        <PhpGroupResponses patient={patient} user={user} notify={notify} navigate={navigate} openAssessments={openAssessments} programType="psr" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Reviews */

function Reviews({ patient, user, notify, canManage }) {
  const [o, setO] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [plan, setPlan] = useState(null);
  const [showRevForm, setShowRevForm] = useState(false);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      if (DEMO) { setO(demoOverview(patient)); setReviews([]); setPlan(null); return; }
      const ov = await api(`/patients/${patient.id}/psr/program`);
      setO(ov);
      if (ov.treatment_plan?.id) {
        const [rv, pl] = await Promise.all([
          api(`/patients/${patient.id}/php/treatment-plans/${ov.treatment_plan.id}/reviews`),
          api(`/patients/${patient.id}/php/treatment-plans`),
        ]);
        setReviews(rv.reviews || []);
        setPlan((pl.plans || []).find(p => p.id === ov.treatment_plan.id) || null);
      } else { setReviews([]); setPlan(null); }
    } catch (err) { notify(err.message); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patient.id]);

  async function recordReview(payload) {
    if (!o?.treatment_plan?.id) { notify('No treatment plan to review.'); return; }
    setBusy(true);
    try {
      if (DEMO) { notify('Review recorded (demo).'); setShowRevForm(false); return; }
      const body = { ...payload, plan_id: o.treatment_plan.id };
      await api(`/patients/${patient.id}/php/treatment-plans/${o.treatment_plan.id}/reviews`, { method: 'POST', body });
      notify('Review recorded.');
      setShowRevForm(false);
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  if (!o) return <p className="empty-copy">Loading…</p>;
  const nextReview = o.treatment_plan?.next_review_due_at;

  return (
    <div>
      <div className="content-heading"><div><h4>Treatment plan reviews</h4><p>The review due date is set explicitly by the clinician — never derived from goal or objective target dates. PSR has no physician recertification requirement.</p></div><div className="php-head-actions">{nextReview ? <span className="badge badge-neutral">Next review: {new Date(nextReview).toLocaleDateString()}</span> : <span className="badge badge-neutral">Not scheduled</span>}{canManage && !showRevForm && plan && <button type="button" className="button primary" onClick={() => setShowRevForm(true)}><Plus size={14} /><span>Record review</span></button>}</div></div>

      {reviews.length === 0 && <p className="empty-copy">No reviews recorded yet.</p>}
      {reviews.map(rv => (
        <div key={rv.id} className="php-plan">
          <div className="php-plan-head">
            <span className="badge badge-neutral">{rv.review_type}</span>
            <small>{new Date(rv.reviewed_at).toLocaleString()}</small>
            <small>· {rv.reviewer_name || '—'}</small>
          </div>
          <div className="php-problem">
            {rv.notes && <p><strong>Notes.</strong> {rv.notes}</p>}
            {rv.crisis_relapse && <p><strong>Crisis / relapse plan.</strong> {rv.crisis_relapse}</p>}
            {rv.discharge_plan && <p><strong>Discharge plan.</strong> {rv.discharge_plan}</p>}
            {(rv.goals || []).map(g => (
              <div key={g.id} className="php-goal">
                <span>{g.goal_description || 'Goal'}{g.overall_progress ? <small> · {g.overall_progress}</small> : null}</span>
                {g.notes && <small className="clip" style={{ display: 'block' }}>{g.notes}</small>}
                {(g.objectives || []).map(o => (
                  <div key={o.id} className="php-obj">
                    <span>{o.objective_description || 'Objective'}{o.status ? <small> · {o.status}</small> : null}</span>
                    {o.progress && <small className="clip" style={{ display: 'block' }}>Progress: {o.progress}</small>}
                    {o.regression && <small className="clip" style={{ display: 'block' }}>Regression: {o.regression}</small>}
                    {o.narrative && <small className="clip" style={{ display: 'block' }}>{o.narrative}</small>}
                    {o.barriers && <small className="clip" style={{ display: 'block' }}>Barriers: {o.barriers}</small>}
                    {o.revised_target_date && <small className="clip" style={{ display: 'block' }}>Revised target: {o.revised_target_date}</small>}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      ))}

      {showRevForm && plan && (
        <TreatmentPlanReviewForm plan={plan} busy={busy} onSubmit={recordReview} onCancel={() => setShowRevForm(false)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Discharge */

function Discharge({ patient, user, notify, canManage }) {
  const [enrollments, setEnrollments] = useState([]);
  const [discharges, setDischarges] = useState([]);
  const [phq9, setPhq9] = useState(null);
  const [phq9Change, setPhq9Change] = useState(null);
  const [form, setForm] = useState({ enrollment_id: '', discharge_date: new Date().toISOString().slice(0, 10), discharge_reason: '', discharge_disposition: '', condition_at_discharge: '', goals_not_achieved: '', safety_status: '', follow_up_appointments: '', next_level_of_care: '', outpatient_providers: '', community_referrals: '', patient_education: '', family_education: '', relapse_safety_plan: '', outcome: '', treatment_summary: '', services_received: '', goal_outcomes: '', finalize: true });
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      if (DEMO) { setEnrollments([]); setDischarges([]); setPhq9(null); setPhq9Change(null); return; }
      const [e, o, d] = await Promise.all([
        api(`/patients/${patient.id}/psr/enrollments`),
        api(`/patients/${patient.id}/psr/program`),
        api(`/patients/${patient.id}/psr/discharges`),
      ]);
      setEnrollments(e.enrollments || []);
      setDischarges(d.discharges || []);
      setPhq9(o.latest_phq9 || null);
      setPhq9Change(o.phq9_change);
    } catch (err) { notify(err.message); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [patient.id]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault(); setBusy(true);
    try {
      if (DEMO) { notify('Discharge recorded (demo).'); return; }
      const body = { ...form, services_received: splitLines(form.services_received), goal_outcomes: splitLines(form.goal_outcomes) };
      await api(`/patients/${patient.id}/psr/discharge`, { method: 'POST', body });
      notify('Discharge recorded. The patient chart remains open.');
      setForm(f => ({ ...f, discharge_reason: '', discharge_disposition: '', condition_at_discharge: '', goals_not_achieved: '', safety_status: '', follow_up_appointments: '', next_level_of_care: '', outpatient_providers: '', community_referrals: '', patient_education: '', family_education: '', relapse_safety_plan: '', outcome: '', treatment_summary: '', services_received: '', goal_outcomes: '' }));
      await load();
    } catch (err) { notify(err.message); } finally { setBusy(false); }
  }

  const active = enrollments.filter(e => e.status === 'active');

  return (
    <div>
      <div className="content-heading"><div><h4>Discharge</h4><p>Sets the enrollment to discharged with a date, reason, and disposition. It never closes or deletes the patient chart.</p></div></div>

      {active.length === 0 && <p className="empty-copy">No active enrollment to discharge.</p>}

      {active.length > 0 && canManage && (
        <form className="php-form" onSubmit={submit}>
          <div className="form-grid">
            <label>Enrollment
              <select value={form.enrollment_id} onChange={set('enrollment_id')}>
                <option value="">— Select —</option>
                {active.map(en => <option key={en.id} value={en.id}>{en.program_type} · admitted {en.admitted_at}</option>)}
              </select>
            </label>
            <label>Discharge date<input type="date" value={form.discharge_date} onChange={set('discharge_date')} /></label>
            <label className="span2">Discharge reason<textarea rows={2} value={form.discharge_reason} onChange={set('discharge_reason')} maxLength={10000} /></label>
            <label className="span2">Discharge disposition<textarea rows={2} value={form.discharge_disposition} onChange={set('discharge_disposition')} maxLength={10000} /></label>
            <label className="span2">Condition at discharge<textarea rows={2} value={form.condition_at_discharge} onChange={set('condition_at_discharge')} maxLength={10000} /></label>
            <label className="span2">Goals not achieved<textarea rows={2} value={form.goals_not_achieved} onChange={set('goals_not_achieved')} maxLength={10000} /></label>
            <label className="span2">Safety status<textarea rows={2} value={form.safety_status} onChange={set('safety_status')} maxLength={10000} /></label>
            <label className="span2">Follow-up appointments<textarea rows={2} value={form.follow_up_appointments} onChange={set('follow_up_appointments')} maxLength={10000} /></label>
            <label className="span2">Next level of care<textarea rows={2} value={form.next_level_of_care} onChange={set('next_level_of_care')} maxLength={10000} /></label>
            <label className="span2">Outpatient providers<textarea rows={2} value={form.outpatient_providers} onChange={set('outpatient_providers')} maxLength={10000} /></label>
            <label className="span2">Community referrals<textarea rows={2} value={form.community_referrals} onChange={set('community_referrals')} maxLength={10000} /></label>
            <label className="span2">Patient education<textarea rows={2} value={form.patient_education} onChange={set('patient_education')} maxLength={10000} /></label>
            <label className="span2">Family education<textarea rows={2} value={form.family_education} onChange={set('family_education')} maxLength={10000} /></label>
            <label className="span2">Relapse / safety plan<textarea rows={2} value={form.relapse_safety_plan} onChange={set('relapse_safety_plan')} maxLength={10000} /></label>
            <label>Outcome
              <select value={form.outcome} onChange={set('outcome')}>
                <option value="">— Unset —</option>
                <option value="successful">Successful</option>
                <option value="unsuccessful">Unsuccessful</option>
                <option value="withdrawal">Withdrawal</option>
                <option value="non_compliance">Non-compliance</option>
                <option value="administrative">Administrative</option>
                <option value="death">Death</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label className="span2">Treatment summary<textarea rows={3} value={form.treatment_summary} onChange={set('treatment_summary')} maxLength={20000} /></label>
            <label className="span2">Services received (one per line)<textarea rows={3} value={form.services_received} onChange={set('services_received')} /></label>
            <label className="span2">Goal outcomes (one per line)<textarea rows={3} value={form.goal_outcomes} onChange={set('goal_outcomes')} /></label>
            <label className="check-row"><input type="checkbox" checked={form.finalize} onChange={e => setForm(f => ({ ...f, finalize: e.target.checked }))} /><span>Finalize on discharge (captures an immutable snapshot of the reviewed canonical state)</span></label>
          </div>
          <button className="button primary" disabled={busy}>{busy ? 'Discharging…' : 'Discharge'}</button>
        </form>
      )}

      {active.length > 0 && phq9 && (
        <div className="php-ov-section section-space">
          <div className="content-heading"><div><h4>PHQ-9 outcome comparison</h4><p>Supporting screening reference only — never an automatic determination of program success.</p></div></div>
          <div className="php-phq9-compare">
            <div className="card">
              <small>Most recent PHQ-9</small>
              <strong>{phq9.total ?? '—'}</strong>
              <span className="badge badge-neutral">{phq9.severity || '—'}</span>
              {phq9.completed_at && <small>{new Date(phq9.completed_at).toLocaleDateString()}</small>}
            </div>
            {phq9Change != null && (
              <div className="card">
                <small>Change from previous</small>
                <strong className={phq9Change < 0 ? 'improved' : phq9Change > 0 ? 'worsened' : ''}>{phq9Change > 0 ? '+' : ''}{phq9Change}</strong>
              </div>
            )}
          </div>
        </div>
      )}

      {discharges.length > 0 && (
        <div className="section-space">
          <div className="content-heading"><div><h4>Discharge records</h4><p>Finalized discharges are immutable — corrections are versioned addenda, never overwrites.</p></div></div>
          {discharges.map(d => (
            <div key={d.id} className="php-plan">
              <div className="php-plan-head">
                {statusBadge(d.status)}
                <strong>Version {d.version}</strong>
                {d.finalized_at && <small>Finalized {d.finalized_at}</small>}
              </div>
              <div className="php-problem">
                <strong className="php-problem-desc">{d.condition_at_discharge || '—'}</strong>
                {d.outcome && <small>Outcome: {d.outcome}</small>}
                {d.treatment_summary && <p><strong>Treatment summary.</strong> {d.treatment_summary}</p>}
                {(d.services_received || []).length > 0 && <p><strong>Services received.</strong> {d.services_received.join('; ')}</p>}
                {(d.goal_outcomes || []).length > 0 && <p><strong>Goal outcomes.</strong> {d.goal_outcomes.join('; ')}</p>}
                <small>Recorded by {d.creator_name || '—'}</small>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Audit */

function Audit({ patient, user, notify }) {
  const canViewAudit = hasCap(user, 'audit.view.facility');
  const [data, setData] = useState({ enrollments: [], admissions: [] });
  const [audit, setAudit] = useState([]);
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (DEMO) { setData({ enrollments: [], admissions: [] }); setAudit([]); return; }
    if (canViewAudit) {
      api(`/patients/${patient.id}/psr/audit`).then(a => setAudit(a.audit || [])).catch(err => notify(err.message));
    } else {
      Promise.all([
        api(`/patients/${patient.id}/psr/enrollments`),
        api(`/patients/${patient.id}/psr/admissions`),
      ]).then(([e, a]) => setData({ enrollments: e.enrollments || [], admissions: a.admissions || [] })).catch(err => notify(err.message));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.id, canViewAudit]);

  const filtered = audit.filter(a => !filter || (a.action || '').toLowerCase().includes(filter.toLowerCase()) || (a.actor_name || '').toLowerCase().includes(filter.toLowerCase()));

  return (
    <div>
      <div className="content-heading"><div><h4>Program audit trail</h4><p>Read-only history of the canonical episode records. Every admission, discharge, plan version, and service is retained append-only and independently audited.</p></div></div>

      {canViewAudit ? (
        <div className="section-space">
          <div className="filter-inline"><input aria-label="Filter audit events" value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter by action or actor" /></div>
          {filtered.length === 0 && <p className="empty-copy">{filter ? 'No matching audit events.' : 'No audit events recorded for this patient.'}</p>}
          {filtered.length > 0 && (
            <div className="table-scroll"><table>
              <thead><tr><th>When</th><th>Action</th><th>Actor</th><th>Details</th></tr></thead>
              <tbody>{filtered.map(a => (
                <tr key={a.id}>
                  <td>{a.created_at ? new Date(a.created_at).toLocaleString() : '—'}</td>
                  <td><code>{a.action}</code></td>
                  <td>{a.actor_name || '—'}</td>
                  <td>
                    <button type="button" className="link" onClick={() => setExpanded(expanded === a.id ? null : a.id)}>{expanded === a.id ? 'Hide' : 'View'}</button>
                    {expanded === a.id && <pre className="audit-details">{JSON.stringify(a.details || {}, null, 2)}</pre>}
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      ) : (
        <>
          <div className="section-space">
            <h4>Enrollments</h4>
            {data.enrollments.length === 0 && <p className="empty-copy">None.</p>}
            {data.enrollments.length > 0 && (
              <div className="table-scroll"><table>
                <thead><tr><th>ID</th><th>Program</th><th>Admitted</th><th>Discharged</th><th>Status</th><th>Updated by</th></tr></thead>
                <tbody>{data.enrollments.map(en => (
                  <tr key={en.id}><td>{en.id}</td><td>{en.program_type}</td><td>{en.admitted_at || '—'}</td><td>{en.discharge_date || '—'}</td><td>{en.status}</td><td>{en.updated_by || '—'}</td></tr>
                ))}</tbody>
              </table></div>
            )}
          </div>
          <div className="section-space">
            <h4>Admissions</h4>
            {data.admissions.length === 0 && <p className="empty-copy">None.</p>}
            {data.admissions.length > 0 && (
              <div className="table-scroll"><table>
                <thead><tr><th>ID</th><th>Admitted</th><th>Admitting physician</th><th>Recorded by</th></tr></thead>
                <tbody>{data.admissions.map(a => (
                  <tr key={a.id}><td>{a.id}</td><td>{a.admitted_on}</td><td>{a.admitting_name || '—'}</td><td>{a.creator_name || '—'}</td></tr>
                ))}</tbody>
              </table></div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
